import { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Check, History, Pencil, Plus, X } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { ESTADO_BADGE, ESTADO_INTERCAMBIO, ESTADO_INTERCAMBIO_BADGE, ESTADO_SOLICITUD, fmtFecha, fmtHora, hoyISO, subtituloVacaciones, TIPO_SOLICITUD, fmtHoras, etiquetaEstadoSolicitud } from "@/lib/format";
import { agruparSolicitudes } from "@/lib/solicitudesAgrupar";
import { useEmpresa } from "@/hooks/useEmpresa";
import IntercambioTurnoDialog from "@/components/IntercambioTurnoDialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import CompensacionSaldoPanel from "@/components/CompensacionSaldoPanel";
import AusenciaParcialWorkerPanel from "@/components/AusenciaParcialWorkerPanel";
import MotivoAusenciaJustificadaFields from "@/components/MotivoAusenciaJustificadaFields";

function diasEnRangoISO(desde, hasta) {
  const out = [];
  const d = new Date(`${desde}T12:00:00`);
  const fin = new Date(`${hasta}T12:00:00`);
  while (d <= fin) {
    out.push(d.toISOString().slice(0, 10));
    d.setDate(d.getDate() + 1);
  }
  return out;
}

function nowLocalInput() {
  const d = new Date();
  d.setMinutes(d.getMinutes() - d.getTimezoneOffset());
  return d.toISOString().slice(0, 16);
}

function minutosFranjas(franjas) {
  if (!Array.isArray(franjas)) return 0;
  return franjas.reduce((acc, f) => {
    if (!f?.inicio || !f?.fin) return acc;
    const [h1, m1] = f.inicio.split(":").map(Number);
    const [h2, m2] = f.fin.split(":").map(Number);
    return acc + (h2 * 60 + m2 - (h1 * 60 + m1));
  }, 0);
}

function fmtMinutos(m) {
  if (m == null || Number.isNaN(m)) return "—";
  const h = Math.floor(m / 60);
  const min = m % 60;
  if (min === 0) return `${h} h`;
  return `${h} h ${min} min`;
}

function labelFranjas(franjas) {
  if (!franjas?.length) return "—";
  return franjas.map((f) => `${f.inicio}–${f.fin}`).join(" · ");
}

function TarjetaSolicitudWorker({ s, onActualizar }) {
  return (
    <div
      className={`rounded-xl border bg-white p-4 shadow-sm ${
        s.notificacion_pendiente ? "border-indigo-300 ring-1 ring-indigo-200" : "border-zinc-200"
      }`}
    >
      <div className="flex items-center justify-between gap-2">
        <Badge variant="outline" className="border-violet-200 bg-violet-50 text-violet-800">
          {s.tipo_label || TIPO_SOLICITUD[s.tipo]}
        </Badge>
        <Badge variant="outline" className={ESTADO_BADGE[s.estado]}>
          {etiquetaEstadoSolicitud(s)}
        </Badge>
      </div>
      {(s.tipo === "fichaje_manual" || s.tipo === "fuera_horario" || s.tipo === "modificacion_fichaje") ? (
        <p className="mt-2 text-sm text-zinc-600">
          {s.tipo === "modificacion_fichaje" && s.timestamp_solicitado && s.timestamp_aprobado ? (
            <>
              Cambio:{" "}
              <span className="line-through text-zinc-400">
                {fmtFecha(s.timestamp_solicitado)} {fmtHora(s.timestamp_solicitado)}
              </span>
              {" → "}
              <strong>
                {fmtFecha(s.timestamp_aprobado)} {fmtHora(s.timestamp_aprobado)}
              </strong>
              {s.tipo_fichaje ? ` (${s.tipo_fichaje})` : ""}
            </>
          ) : s.tipo === "fichaje_manual" && s.timestamp_solicitado ? (
            <>
              Añadir {s.tipo_fichaje || "picado"}:{" "}
              <strong>
                {fmtFecha(s.timestamp_solicitado)} {fmtHora(s.timestamp_solicitado)}
              </strong>
            </>
          ) : (
            <>
              {s.tipo === "modificacion_fichaje" && s.timestamp_solicitado && (
                <>Anterior: {fmtFecha(s.timestamp_solicitado)} {fmtHora(s.timestamp_solicitado)} · </>
              )}
              {s.timestamp_aprobado || s.timestamp_solicitado
                ? `${fmtFecha(s.timestamp_aprobado || s.timestamp_solicitado)} ${fmtHora(s.timestamp_aprobado || s.timestamp_solicitado)}${s.tipo_fichaje ? ` (${s.tipo_fichaje})` : ""}`
                : null}
            </>
          )}
        </p>
      ) : s.tipo === "hora_extra" ? (
        <p className="mt-2 text-sm text-zinc-600">
          {s.fecha_fin && s.fecha_fin !== s.fecha_inicio
            ? `${fmtFecha(s.fecha_inicio)} — ${fmtFecha(s.fecha_fin)}`
            : fmtFecha(s.fecha_inicio)}
          {s.segundos != null ? ` · ${fmtHoras(s.segundos)} de exceso` : ""}
          {s.detalle?.modo === "flexible_periodo"
            ? ` · cómputo ${s.detalle.computo === "mes" ? "mensual" : "semanal"}`
            : ""}
        </p>
      ) : s.tipo === "cambio_jornada" ? (
        <div className="mt-2 space-y-1 text-sm text-zinc-600">
          <p>
            {fmtFecha(s.fecha_inicio)}
            {s.fecha_fin !== s.fecha_inicio ? ` — ${fmtFecha(s.fecha_fin)}` : ""}
            {s.dias != null ? ` · ${s.dias} día(s)` : ""}
          </p>
          {(s.cambio_jornada || s.detalle)?.franjas && (
            <p>
              Nueva jornada:{" "}
              <strong>{(s.cambio_jornada || s.detalle).franjas_label || labelFranjas((s.cambio_jornada || s.detalle).franjas)}</strong>
              {(s.cambio_jornada || s.detalle).minutos != null
                ? ` (${fmtMinutos((s.cambio_jornada || s.detalle).minutos)})`
                : ""}
            </p>
          )}
        </div>
      ) : (
        <p className="mt-2 text-sm text-zinc-600">
          {fmtFecha(s.fecha_inicio)} — {fmtFecha(s.fecha_fin)} · {s.dias} días
        </p>
      )}
      {s.motivo && <p className="mt-1 text-sm text-zinc-500">{s.motivo}</p>}
      {s.revisado_por_nombre && <p className="mt-1 text-xs text-indigo-600">Revisado por: {s.revisado_por_nombre}</p>}
      {s.estado === "aprobada" && s.fichaje_id && (
        <p className="mt-1 text-xs text-green-700">Picado registrado · fichaje #{s.fichaje_id}</p>
      )}
      {s.comentario_revision && (
        <p className="mt-2 rounded-lg bg-zinc-50 px-3 py-2 text-xs text-zinc-600">Respuesta: {s.comentario_revision}</p>
      )}
      {s.tipo === "ausencia_justificada" && s.ausencia_unidades?.length > 0 && (
        <ul className="mt-2 space-y-1 text-xs text-zinc-600">
          {s.ausencia_unidades.map((u) => (
            <li key={u.id}>
              · {u.label}{" "}
              <span className="text-zinc-400">
                ({u.estado === "aprobada" ? "aprobado" : u.estado === "rechazada" ? "no aprobado" : u.estado})
              </span>
            </li>
          ))}
        </ul>
      )}
      <AusenciaParcialWorkerPanel solicitud={s} onActualizar={onActualizar} />
    </div>
  );
}

function FilaFichajeWorker({ s }) {
  return (
    <li className="rounded-lg bg-zinc-50 px-3 py-2 text-sm">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="min-w-0">
          <span className="font-medium text-zinc-900">
            {s.timestamp_solicitado ? fmtHora(s.timestamp_solicitado) : "—"}
          </span>
          <span className="ml-2 text-zinc-600">({s.tipo_fichaje})</span>
          {s.timestamp_real && (
            <span className="ml-2 text-xs text-zinc-400">
              enviada {fmtFecha(s.timestamp_real)} {fmtHora(s.timestamp_real)}
            </span>
          )}
        </div>
        <Badge variant="outline" className={`shrink-0 text-[10px] ${ESTADO_BADGE[s.estado] || ""}`}>
          {etiquetaEstadoSolicitud(s)}
        </Badge>
      </div>
      {s.motivo && <p className="mt-0.5 text-xs text-zinc-500">{s.motivo}</p>}
      {s.estado === "aprobada" && s.fichaje_id && (
        <p className="mt-1 text-xs text-green-700">Picado #{s.fichaje_id}</p>
      )}
      {s.comentario_revision && (
        <p className="mt-1 text-xs text-indigo-700">{s.comentario_revision}</p>
      )}
    </li>
  );
}

function TarjetaGrupoWorker({ grupo }) {
  const { items, tipo_label, tipo, dia } = grupo;
  const diaFmt = dia ? fmtFecha(`${dia}T12:00:00`) : null;
  const tieneNotif = items.some((s) => s.notificacion_pendiente);
  const estadoUnico = items.every((s) => s.estado === items[0].estado) ? items[0].estado : null;

  return (
    <div
      className={`rounded-xl border bg-white p-4 shadow-sm ${
        tieneNotif ? "border-indigo-300 ring-1 ring-indigo-200" : "border-violet-200"
      }`}
      data-testid={`mis-solicitudes-grupo-${tipo}-${dia || "sin-dia"}`}
    >
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex flex-wrap items-center gap-2">
          <Badge variant="outline" className="border-violet-200 bg-violet-50 text-violet-800">
            {tipo_label || TIPO_SOLICITUD[tipo]}
          </Badge>
          {diaFmt && <span className="text-xs font-medium text-zinc-500">{diaFmt}</span>}
          <Badge variant="outline" className="border-zinc-200 bg-zinc-50 text-zinc-700">
            {items.length} solicitud{items.length > 1 ? "es" : ""}
          </Badge>
        </div>
        {estadoUnico && (
          <Badge variant="outline" className={ESTADO_BADGE[estadoUnico]}>
            {etiquetaEstadoSolicitud(items[0])}
          </Badge>
        )}
      </div>
      <ul className="mt-3 space-y-2">
        {items.map((s) => (
          <FilaFichajeWorker key={s.id} s={s} />
        ))}
      </ul>
    </div>
  );
}

function renderSolicitudEntrada(entrada, onActualizar) {
  if (entrada.kind === "group") {
    return <TarjetaGrupoWorker key={entrada.key} grupo={entrada} />;
  }
  return <TarjetaSolicitudWorker key={entrada.item.id} s={entrada.item} onActualizar={onActualizar} />;
}

export default function MisSolicitudes() {
  const { empresa } = useEmpresa();
  const [searchParams, setSearchParams] = useSearchParams();
  const gestionarZonas = !!empresa?.gestionar_zonas;
  const [solicitudes, setSolicitudes] = useState([]);
  const [saldo, setSaldo] = useState(null);
  const [saldoAsuntos, setSaldoAsuntos] = useState(null);
  const [intercambios, setIntercambios] = useState([]);
  const [intercambioOpen, setIntercambioOpen] = useState(false);
  const [open, setOpen] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(null);
  const [comentarioConf, setComentarioConf] = useState("");
  const [form, setForm] = useState({
    tipo: "vacaciones",
    fecha_inicio: hoyISO(),
    fecha_fin: hoyISO(),
    motivo: "",
    tipo_fichaje: "entrada",
    timestamp_solicitado: nowLocalInput(),
    justificada: true,
    alcance_ausencia: "dia_completo",
    franjas: [{ inicio: "07:00", fin: "15:00" }],
  });
  const [previewCj, setPreviewCj] = useState(null);
  const [compensaciones, setCompensaciones] = useState([]);
  const [saldoExtra, setSaldoExtra] = useState(null);
  const [compOpen, setCompOpen] = useState(false);
  const [horaExtraOpen, setHoraExtraOpen] = useState(false);
  const [horaExtraDias, setHoraExtraDias] = useState([]);
  const [horaExtraUmbral, setHoraExtraUmbral] = useState(15);
  const [horaExtraModo, setHoraExtraModo] = useState("dia");
  const [horaExtraDia, setHoraExtraDia] = useState(null);
  const [horaExtraMotivo, setHoraExtraMotivo] = useState("");
  const [horaExtraCargando, setHoraExtraCargando] = useState(false);
  const [compForm, setCompForm] = useState({
    horas: "1",
    modalidad: "periodo",
    fecha_disfrute_inicio: hoyISO(),
    fecha_disfrute_fin: hoyISO(),
    motivo: "",
  });
  const [compConfirmOpen, setCompConfirmOpen] = useState(null);
  const [compCalculo, setCompCalculo] = useState(null);
  const [misVacaciones, setMisVacaciones] = useState([]);
  const [filtroEstado, setFiltroEstado] = useState("todas");
  const [vacAjusteOpen, setVacAjusteOpen] = useState(null);
  const [vacAjusteForm, setVacAjusteForm] = useState({
    accion: "modificar_periodo",
    fecha_inicio: hoyISO(),
    fecha_fin: hoyISO(),
    dias: [],
    motivo: "",
  });

  const cargar = useCallback(async () => {
    try {
      const [s, b, sa, comps, extra, vacs, inter] = await Promise.all([
        api.get("/solicitudes/mis"),
        api.get("/solicitudes/saldo"),
        api.get("/solicitudes/saldo-asuntos").catch(() => ({ data: null })),
        api.get("/compensaciones/mias").catch(() => ({ data: [] })),
        api.get("/compensaciones/saldo").catch(() => ({ data: null })),
        api.get("/vacaciones/mis").catch(() => ({ data: [] })),
        api.get("/intercambios/mias").catch(() => ({ data: [] })),
      ]);
      setSolicitudes(s.data);
      setSaldo(b.data);
      setSaldoAsuntos(sa.data);
      setCompensaciones(comps.data || []);
      setSaldoExtra(extra.data);
      setMisVacaciones(vacs.data || []);
      setIntercambios(inter.data || []);
      const teniaNoLeidas = s.data.some((x) => x.notificacion_pendiente);
      if (teniaNoLeidas) {
        await api.put("/solicitudes/notificaciones/leidas");
        setSolicitudes((prev) =>
          prev.map((x) => (x.notificacion_pendiente ? { ...x, notificacion_pendiente: false, revision_leida: true } : x))
        );
        window.dispatchEvent(new Event("notificaciones-actualizar"));
      }
    } catch (e) {
      toast.error(formatApiError(e));
    }
  }, []);

  useEffect(() => { cargar(); }, [cargar]);

  // Prefill desde Cuadro / retrasos: ?nueva=ausencia_justificada&alcance=llegada_tardia&fecha=YYYY-MM-DD
  useEffect(() => {
    const nueva = searchParams.get("nueva");
    if (nueva !== "ausencia_justificada") return;
    const alcance = searchParams.get("alcance") || "llegada_tardia";
    const fecha = searchParams.get("fecha") || hoyISO();
    const alcanceOk = ["dia_completo", "llegada_tardia"].includes(alcance) ? alcance : "llegada_tardia";
    setForm((prev) => ({
      ...prev,
      tipo: "ausencia_justificada",
      alcance_ausencia: alcanceOk,
      fecha_inicio: fecha,
      fecha_fin: fecha,
      motivo: prev.motivo || "",
    }));
    setOpen(true);
    setSearchParams({}, { replace: true });
  }, [searchParams, setSearchParams]);

  const asuntosPropiosActivo = saldoAsuntos?.activo === true;

  useEffect(() => {
    if (!asuntosPropiosActivo && form.tipo === "asuntos_propios") {
      setForm((prev) => ({ ...prev, tipo: "vacaciones" }));
    }
  }, [asuntosPropiosActivo, form.tipo]);

  const esFichaje = form.tipo === "fichaje_manual" || form.tipo === "fuera_horario";
  const esAusenciaJustificada = form.tipo === "ausencia_justificada";
  const esCambioJornada = form.tipo === "cambio_jornada";
  const esLlegadaTardia = esAusenciaJustificada && form.alcance_ausencia === "llegada_tardia";
  const minutosPropuestos = useMemo(() => minutosFranjas(form.franjas), [form.franjas]);

  useEffect(() => {
    if (!open || !esCambioJornada || !form.fecha_inicio || !form.fecha_fin) {
      setPreviewCj(null);
      return;
    }
    let cancel = false;
    api
      .get("/solicitudes/cambio-jornada/preview", {
        params: { fecha_inicio: form.fecha_inicio, fecha_fin: form.fecha_fin },
      })
      .then(({ data }) => {
        if (!cancel) setPreviewCj(data);
      })
      .catch(() => {
        if (!cancel) setPreviewCj(null);
      });
    return () => {
      cancel = true;
    };
  }, [open, esCambioJornada, form.fecha_inicio, form.fecha_fin]);

  const minFechaSolicitud = useMemo(() => {
    if (form.tipo === "vacaciones" || form.tipo === "baja") {
      const d = new Date();
      d.setDate(d.getDate() + 1);
      return d.toISOString().slice(0, 10);
    }
    if (form.tipo === "ausencia_justificada") {
      const d = new Date();
      d.setDate(d.getDate() - 30);
      return d.toISOString().slice(0, 10);
    }
    return hoyISO();
  }, [form.tipo]);

  const maxFechaSolicitud = useMemo(() => {
    if (form.tipo === "ausencia_justificada" && form.alcance_ausencia === "llegada_tardia") {
      return hoyISO();
    }
    return undefined;
  }, [form.tipo, form.alcance_ausencia]);

  const maxFechaVacaciones = useMemo(() => {
    const y = new Date().getFullYear() + 1;
    return `${y}-12-31`;
  }, []);

  const anioFormulario = useMemo(() => {
    if (!form.fecha_inicio || form.fecha_inicio.length < 4) return new Date().getFullYear();
    return Number(form.fecha_inicio.slice(0, 4));
  }, [form.fecha_inicio]);

  useEffect(() => {
    if (!open || form.tipo !== "vacaciones") return;
    let cancel = false;
    api
      .get("/solicitudes/saldo", { params: { anio: anioFormulario } })
      .then(({ data }) => {
        if (!cancel) setSaldo(data);
      })
      .catch(() => {});
    return () => {
      cancel = true;
    };
  }, [open, form.tipo, anioFormulario]);

  const crear = async () => {
    if (!form.motivo.trim() || form.motivo.trim().length < 3) {
      toast.error("Indica el motivo (mínimo 3 caracteres)");
      return;
    }
    try {
      if (esCambioJornada) {
        const franjasOk = (form.franjas || []).filter((f) => f.inicio && f.fin);
        if (!franjasOk.length) {
          toast.error("Indica al menos una franja horaria");
          return;
        }
        if (previewCj && previewCj.minutos_referencia != null && minutosPropuestos !== previewCj.minutos_referencia) {
          toast.error(
            `La duración propuesta (${fmtMinutos(minutosPropuestos)}) debe coincidir con la habitual (${fmtMinutos(previewCj.minutos_referencia)})`
          );
          return;
        }
      }
      const payload = { tipo: form.tipo, motivo: form.motivo };
      if (esFichaje) {
        payload.tipo_fichaje = form.tipo_fichaje;
        payload.timestamp_solicitado = form.timestamp_solicitado;
        if (form.tipo === "fuera_horario") payload.justificada = form.justificada;
      } else {
        payload.fecha_inicio = form.fecha_inicio;
        payload.fecha_fin = esLlegadaTardia ? form.fecha_inicio : form.fecha_fin;
        if (esAusenciaJustificada) {
          payload.alcance_ausencia = form.alcance_ausencia || "dia_completo";
        }
        if (esCambioJornada) {
          payload.franjas = (form.franjas || []).filter((f) => f.inicio && f.fin);
        }
      }
      const { data } = await api.post("/solicitudes", payload);
      setOpen(false);
      setPreviewCj(null);
      setForm({
        tipo: "vacaciones",
        fecha_inicio: hoyISO(),
        fecha_fin: hoyISO(),
        motivo: "",
        tipo_fichaje: "entrada",
        timestamp_solicitado: nowLocalInput(),
        justificada: true,
        alcance_ausencia: "dia_completo",
        franjas: [{ inicio: "07:00", fin: "15:00" }],
      });
      toast.success(
        data?.estado === "aprobada"
          ? (esFichaje ? "Picado registrado" : "Solicitud registrada")
          : "Solicitud enviada a tu encargado"
      );
      cargar();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const confirmar = async (s, aceptada) => {
    try {
      await api.put(`/solicitudes/${s.id}/confirmar`, { aceptada, comentario: comentarioConf });
      const esMod = s.tipo === "modificacion_fichaje";
      toast.success(
        aceptada
          ? esMod
            ? "Modificación confirmada y aplicada al fichaje"
            : "Fichaje confirmado y registrado"
          : esMod
            ? "Modificación rechazada; el responsable será notificado"
            : "Propuesta rechazada; el encargado será notificado"
      );
      setConfirmOpen(null);
      setComentarioConf("");
      cargar();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const pendientesConfirmacion = solicitudes.filter((s) => s.estado === "pendiente_confirmacion");
  const pendientesConfirmacionAgrupadas = useMemo(
    () => agruparSolicitudes(pendientesConfirmacion),
    [pendientesConfirmacion]
  );
  const solicitudesHistorial = useMemo(
    () => solicitudes.filter((s) => s.estado !== "pendiente_confirmacion"),
    [solicitudes]
  );
  const coincideFiltroEstado = useCallback((s) => {
    switch (filtroEstado) {
      case "pendiente":
        return s.estado === "pendiente";
      case "aprobada":
        return s.estado === "aprobada";
      case "rechazada":
        return s.estado === "rechazada" || s.estado === "denegada_trabajador";
      default:
        return true;
    }
  }, [filtroEstado]);
  const conteosEstado = useMemo(() => ({
    todas: solicitudesHistorial.length,
    pendiente: solicitudesHistorial.filter((s) => s.estado === "pendiente").length,
    aprobada: solicitudesHistorial.filter((s) => s.estado === "aprobada").length,
    rechazada: solicitudesHistorial.filter((s) => s.estado === "rechazada" || s.estado === "denegada_trabajador").length,
  }), [solicitudesHistorial]);
  const solicitudesHistorialFiltradas = useMemo(
    () => solicitudesHistorial.filter(coincideFiltroEstado),
    [solicitudesHistorial, coincideFiltroEstado]
  );
  const solicitudesAgrupadas = useMemo(
    () => agruparSolicitudes(solicitudesHistorialFiltradas),
    [solicitudesHistorialFiltradas]
  );
  const compPendConfirmar = compensaciones.filter((c) => c.estado === "pendiente_trabajador");
  const intercambiosVisibles = useMemo(
    () => (gestionarZonas ? intercambios : intercambios.filter((i) => i.ambito === "turno")),
    [intercambios, gestionarZonas]
  );
  const intercambiosPendientesPareja = intercambiosVisibles.filter((i) => i.requiere_mi_respuesta);

  const responderIntercambio = async (item, acepta) => {
    try {
      const path = item.ambito === "turno"
        ? `/intercambios/turno/${item.id}/responder-pareja`
        : `/intercambios/zona/${item.id}/responder-pareja`;
      await api.put(path, { acepta, comentario: "" });
      toast.success(acepta ? "Has aceptado el intercambio" : "Has rechazado el intercambio");
      cargar();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const abrirHoraExtra = async () => {
    setHoraExtraOpen(true);
    setHoraExtraDia(null);
    setHoraExtraMotivo("");
    setHoraExtraCargando(true);
    try {
      const { data } = await api.get("/solicitudes/horas-extra/dias");
      setHoraExtraDias(data.dias || []);
      setHoraExtraUmbral(data.umbral_minutos ?? 15);
      setHoraExtraModo(data.modo || "dia");
    } catch (e) {
      toast.error(formatApiError(e));
      setHoraExtraDias([]);
    } finally {
      setHoraExtraCargando(false);
    }
  };

  const solicitarHoraExtra = async () => {
    if (!horaExtraDia) {
      toast.error(horaExtraModo === "flexible_periodo" ? "Selecciona un periodo" : "Selecciona un día");
      return;
    }
    if (!horaExtraMotivo.trim() || horaExtraMotivo.trim().length < 3) {
      toast.error("Indica el motivo (mínimo 3 caracteres)");
      return;
    }
    const item = horaExtraDias.find(
      (d) => d.fecha === horaExtraDia || d.periodo_inicio === horaExtraDia
    );
    try {
      await api.post("/solicitudes", {
        tipo: "hora_extra",
        fecha_inicio: item?.periodo_inicio || horaExtraDia,
        fecha_fin: item?.periodo_fin || horaExtraDia,
        motivo: horaExtraMotivo.trim(),
      });
      toast.success("Solicitud de autorización enviada");
      setHoraExtraOpen(false);
      cargar();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const solicitarCompensacion = async () => {
    const segundos = compCalculo?.segundos_propuestos ?? Math.round(Number(compForm.horas) * 3600);
    if (!segundos || segundos <= 0) {
      toast.error("Indica las horas a compensar");
      return;
    }
    if (compCalculo?.excede) {
      toast.error(`Solo dispones de ${fmtHoras(compCalculo.disponible_segundos)} en este periodo`);
      return;
    }
    if (!compForm.motivo.trim() || compForm.motivo.trim().length < 3) {
      toast.error("Indica el motivo");
      return;
    }
    try {
      await api.post("/compensaciones/solicitar", {
        segundos,
        modalidad: compForm.modalidad,
        fecha_disfrute_inicio: compForm.fecha_disfrute_inicio,
        fecha_disfrute_fin: compForm.fecha_disfrute_fin,
        periodo_desde: saldoExtra?.periodo_desde,
        periodo_hasta: saldoExtra?.periodo_hasta,
        motivo: compForm.motivo.trim(),
      });
      toast.success("Solicitud enviada a tu encargado");
      setCompOpen(false);
      setCompCalculo(null);
      cargar();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const abrirAjusteVacaciones = (v) => {
    setVacAjusteOpen(v);
    setVacAjusteForm({
      accion: "modificar_periodo",
      fecha_inicio: v.fecha_inicio,
      fecha_fin: v.fecha_fin,
      dias: diasEnRangoISO(v.fecha_inicio, v.fecha_fin),
      motivo: "",
    });
  };

  const enviarAjusteVacaciones = async () => {
    if (!vacAjusteForm.motivo.trim() || vacAjusteForm.motivo.trim().length < 3) {
      toast.error("Indica el motivo del ajuste");
      return;
    }
    if (vacAjusteForm.accion === "modificar_dias" && !vacAjusteForm.dias.length) {
      toast.error("Selecciona los días que deseas mantener");
      return;
    }
    try {
      const payload = {
        solicitud_id: vacAjusteOpen.solicitud_id,
        accion: vacAjusteForm.accion,
        motivo: vacAjusteForm.motivo.trim(),
      };
      if (vacAjusteForm.accion === "modificar_dias") {
        payload.dias = vacAjusteForm.dias;
      } else if (vacAjusteForm.accion === "modificar_periodo") {
        payload.fecha_inicio = vacAjusteForm.fecha_inicio;
        payload.fecha_fin = vacAjusteForm.fecha_fin;
      }
      await api.post("/vacaciones/ajuste-solicitar", payload);
      toast.success("Ajuste enviado a tu encargado para su aprobación");
      setVacAjusteOpen(null);
      cargar();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const vacacionesAsignadas = misVacaciones.filter((v) => ["aprobada", "pendiente_confirmacion"].includes(v.estado));

  const confirmarCompensacion = async (c, aceptada) => {
    try {
      await api.put(`/compensaciones/${c.id}/confirmar`, { aceptada, comentario: comentarioConf });
      toast.success(aceptada ? "Compensación aceptada" : "Compensación rechazada");
      setCompConfirmOpen(null);
      setComentarioConf("");
      cargar();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  return (
    <div className="space-y-5" data-testid="mis-solicitudes-page">
      <div className="rounded-2xl border border-indigo-100 bg-[#eef0fa] p-4">
        <div className="flex items-start justify-between gap-3">
          <div>
            <h1 className="font-display text-2xl font-semibold tracking-tight text-zinc-900">Solicitudes</h1>
            <p className="text-sm text-indigo-700/80">
              Vacaciones{asuntosPropiosActivo ? ", asuntos propios" : ""}, ausencia justificada, cambio de jornada, fichajes{gestionarZonas ? ", cambios de zona" : ""} e intercambios de turno con compañeros de tu departamento.
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button variant="outline" onClick={abrirHoraExtra} className="rounded-lg border-orange-200 text-orange-800">
              Autorizar horas extra
            </Button>
            <Button variant="outline" onClick={() => setIntercambioOpen(true)} className="rounded-lg border-violet-200 text-violet-700">
              Intercambiar turno
            </Button>
            <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button data-testid="nueva-solicitud-btn" className="gap-1.5 rounded-lg bg-indigo-600 hover:bg-indigo-700">
                <Plus className="h-4 w-4" /> Nueva
              </Button>
            </DialogTrigger>
            <DialogContent data-testid="solicitud-dialog" className="max-h-[90vh] overflow-y-auto">
              <DialogHeader><DialogTitle className="font-display">Nueva solicitud</DialogTitle></DialogHeader>
              <div className="space-y-4">
                <div className="space-y-1.5">
                  <Label>Tipo</Label>
                  <Select value={form.tipo} onValueChange={(v) => setForm({ ...form, tipo: v })}>
                    <SelectTrigger data-testid="solicitud-tipo-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="vacaciones">Vacaciones</SelectItem>
                      {asuntosPropiosActivo && (
                        <SelectItem value="asuntos_propios">Asuntos propios</SelectItem>
                      )}
                      <SelectItem value="ausencia_justificada">Ausencia justificada</SelectItem>
                      <SelectItem value="cambio_jornada">Cambio de jornada</SelectItem>
                      <SelectItem value="fichaje_manual">Fichaje manual</SelectItem>
                      <SelectItem value="fuera_horario">Entrada/salida fuera de horario</SelectItem>
                      <SelectItem value="baja">Baja</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                {esFichaje ? (
                  <>
                    <div className="grid grid-cols-2 gap-3">
                      <div className="space-y-1.5">
                        <Label>Entrada / Salida</Label>
                        <Select value={form.tipo_fichaje} onValueChange={(v) => setForm({ ...form, tipo_fichaje: v })}>
                          <SelectTrigger><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="entrada">Entrada</SelectItem>
                            <SelectItem value="salida">Salida</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-1.5">
                        <Label>Fecha y hora del fichaje</Label>
                        <Input type="datetime-local" value={form.timestamp_solicitado} onChange={(e) => setForm({ ...form, timestamp_solicitado: e.target.value })} />
                      </div>
                    </div>
                    {form.tipo === "fuera_horario" && (
                      <div className="flex items-center justify-between rounded-lg border border-indigo-100 bg-indigo-50/50 px-3 py-2">
                        <div>
                          <p className="text-sm font-medium">Justificada</p>
                          <p className="text-xs text-zinc-500">Si está justificada no cuenta como falta de jornada</p>
                        </div>
                        <Switch checked={form.justificada} onCheckedChange={(v) => setForm({ ...form, justificada: v })} />
                      </div>
                    )}
                  </>
                ) : (
                  <>
                    {esAusenciaJustificada && (
                      <fieldset className="space-y-2">
                        <legend className="text-xs font-semibold uppercase tracking-wide text-zinc-500">Alcance</legend>
                        <label className="flex cursor-pointer items-start gap-2 rounded-lg border border-zinc-200 p-3 has-[:checked]:border-violet-400 has-[:checked]:bg-violet-50">
                          <input
                            type="radio"
                            name="alcance-aj"
                            checked={form.alcance_ausencia === "dia_completo"}
                            onChange={() => setForm({ ...form, alcance_ausencia: "dia_completo" })}
                            className="mt-0.5"
                            data-testid="aj-alcance-dia"
                          />
                          <span>
                            <span className="font-medium text-zinc-900">Día completo</span>
                            <span className="mt-0.5 block text-xs text-zinc-500">
                              Justifica toda la jornada del día (también si no hubo picados).
                            </span>
                          </span>
                        </label>
                        <label className="flex cursor-pointer items-start gap-2 rounded-lg border border-zinc-200 p-3 has-[:checked]:border-violet-400 has-[:checked]:bg-violet-50">
                          <input
                            type="radio"
                            name="alcance-aj"
                            checked={form.alcance_ausencia === "llegada_tardia"}
                            onChange={() =>
                              setForm({
                                ...form,
                                alcance_ausencia: "llegada_tardia",
                                fecha_fin: form.fecha_inicio,
                              })
                            }
                            className="mt-0.5"
                            data-testid="aj-alcance-llegada"
                          />
                          <span>
                            <span className="font-medium text-zinc-900">Llegada tardía (hueco)</span>
                            <span className="mt-0.5 block text-xs text-zinc-500">
                              Cubre desde el inicio del horario hasta tu primera entrada real. Si ese día no hay picado, se tratará como día completo.
                            </span>
                          </span>
                        </label>
                      </fieldset>
                    )}
                    <div className={`grid gap-3 ${esLlegadaTardia ? "grid-cols-1" : "grid-cols-2"}`}>
                      <div className="space-y-1.5">
                        <Label>{esLlegadaTardia ? "Día" : "Desde"}</Label>
                        <Input
                          type="date"
                          value={form.fecha_inicio}
                          min={minFechaSolicitud}
                          max={
                            form.tipo === "vacaciones"
                              ? maxFechaVacaciones
                              : maxFechaSolicitud
                          }
                          onChange={(e) => {
                            const v = e.target.value;
                            setForm({
                              ...form,
                              fecha_inicio: v,
                              fecha_fin: esLlegadaTardia ? v : v > form.fecha_fin ? v : form.fecha_fin,
                            });
                          }}
                          data-testid="solicitud-inicio-input"
                        />
                      </div>
                      {!esLlegadaTardia && (
                        <div className="space-y-1.5">
                          <Label>Hasta</Label>
                          <Input
                            type="date"
                            value={form.fecha_fin}
                            min={form.fecha_inicio || minFechaSolicitud}
                            max={form.tipo === "vacaciones" ? maxFechaVacaciones : undefined}
                            onChange={(e) => setForm({ ...form, fecha_fin: e.target.value })}
                            data-testid="solicitud-fin-input"
                          />
                        </div>
                      )}
                    </div>
                    {form.tipo === "vacaciones" && (
                      <p className="text-xs text-violet-700/90 rounded-lg border border-violet-100 bg-violet-50/60 px-3 py-2">
                        Puedes solicitar el año en curso y el siguiente. Los días se descuentan del cupo del año de cada fecha
                        {saldo?.anticipo
                          ? ` (consultando saldo ${saldo.anio}: ${saldo.restantes} restantes del cupo ${saldo.del_anio ?? saldo.total}).`
                          : saldo?.arrastre
                            ? ` (incluye arrastre ${saldo.arrastre > 0 ? "+" : ""}${saldo.arrastre} del año anterior).`
                            : "."}
                      </p>
                    )}
                    {esAusenciaJustificada && (
                      <p className="text-xs text-violet-700/90 rounded-lg border border-violet-100 bg-violet-50/60 px-3 py-2">
                        Puedes justificar hasta 30 días atrás. Una vez aprobada, las horas se imputan como trabajadas
                        {esLlegadaTardia
                          ? " (solo el hueco hasta tu entrada; los picados reales no se modifican)."
                          : "."}
                      </p>
                    )}
                    {esCambioJornada && (
                      <div className="space-y-3 rounded-lg border border-teal-100 bg-teal-50/50 p-3">
                        <p className="text-xs text-teal-800">
                          Propón otras franjas con la misma duración total que tu jornada habitual. Requiere aprobación.
                        </p>
                        {previewCj?.minutos_referencia != null && (
                          <p className="text-xs text-teal-900">
                            Duración habitual: <strong>{fmtMinutos(previewCj.minutos_referencia)}</strong>
                            {previewCj.duraciones_homogeneas === false && (
                              <span className="text-amber-700"> · Los días del rango no tienen la misma duración</span>
                            )}
                          </p>
                        )}
                        {(form.franjas || []).map((f, idx) => (
                          <div key={idx} className="flex flex-wrap items-end gap-2">
                            <div className="space-y-1">
                              <Label className="text-xs">Inicio</Label>
                              <Input
                                type="time"
                                value={f.inicio}
                                onChange={(e) => {
                                  const next = [...form.franjas];
                                  next[idx] = { ...next[idx], inicio: e.target.value };
                                  setForm({ ...form, franjas: next });
                                }}
                              />
                            </div>
                            <div className="space-y-1">
                              <Label className="text-xs">Fin</Label>
                              <Input
                                type="time"
                                value={f.fin}
                                onChange={(e) => {
                                  const next = [...form.franjas];
                                  next[idx] = { ...next[idx], fin: e.target.value };
                                  setForm({ ...form, franjas: next });
                                }}
                              />
                            </div>
                            {form.franjas.length > 1 && (
                              <Button
                                type="button"
                                variant="outline"
                                size="sm"
                                onClick={() =>
                                  setForm({
                                    ...form,
                                    franjas: form.franjas.filter((_, i) => i !== idx),
                                  })
                                }
                              >
                                Quitar
                              </Button>
                            )}
                          </div>
                        ))}
                        <div className="flex flex-wrap items-center justify-between gap-2">
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={() =>
                              setForm({
                                ...form,
                                franjas: [...form.franjas, { inicio: "15:00", fin: "17:00" }],
                              })
                            }
                          >
                            Añadir franja
                          </Button>
                          <p className="text-xs text-zinc-600">
                            Propuesta: <strong>{fmtMinutos(minutosPropuestos)}</strong>
                            {previewCj?.minutos_referencia != null &&
                              minutosPropuestos !== previewCj.minutos_referencia && (
                                <span className="ml-1 text-amber-700">(no coincide)</span>
                              )}
                          </p>
                        </div>
                        {previewCj?.dias?.length > 0 && (
                          <ul className="max-h-28 space-y-0.5 overflow-y-auto text-[11px] text-zinc-600">
                            {previewCj.dias.map((d) => (
                              <li key={d.fecha}>
                                {fmtFecha(`${d.fecha}T12:00:00`)}:{" "}
                                {d.aplicable
                                  ? `${d.franjas_label} (${fmtMinutos(d.minutos)})`
                                  : d.motivo || "No aplicable"}
                              </li>
                            ))}
                          </ul>
                        )}
                      </div>
                    )}
                  </>
                )}
                <div className="space-y-1.5">
                  {esAusenciaJustificada ? (
                    <MotivoAusenciaJustificadaFields
                      value={form.motivo}
                      onChange={(v) => setForm({ ...form, motivo: v })}
                    />
                  ) : (
                    <>
                      <Label>Motivo</Label>
                      <Textarea placeholder="Describe el motivo" value={form.motivo} onChange={(e) => setForm({ ...form, motivo: e.target.value })} data-testid="solicitud-motivo-input" />
                    </>
                  )}
                </div>
              </div>
              <DialogFooter>
                <Button onClick={crear} data-testid="solicitud-submit-btn" className="bg-indigo-600 hover:bg-indigo-700">Enviar solicitud</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
          </div>
        </div>
      </div>

      {intercambiosPendientesPareja.length > 0 && (
        <div className="rounded-2xl border-2 border-violet-300 bg-violet-50 p-4 shadow-sm">
          <p className="text-sm font-semibold text-violet-900">Intercambios pendientes de tu respuesta</p>
          {intercambiosPendientesPareja.map((i) => (
            <div key={`${i.ambito}-${i.id}`} className="mt-3 rounded-xl border border-violet-200 bg-white p-3">
              <p className="font-medium">
                {i.ambito === "turno" ? "Intercambio de turno" : "Intercambio de zona"} — {i.usuario}
              </p>
              <p className="text-sm text-zinc-500">
                {fmtFecha(i.fecha_inicio)}
                {i.fecha_fin !== i.fecha_inicio ? ` — ${fmtFecha(i.fecha_fin)}` : ""}
                {i.hora_inicio ? ` · ${i.hora_inicio}–${i.hora_fin}` : ""}
              </p>
              {i.motivo && <p className="mt-1 text-xs text-zinc-500">{i.motivo}</p>}
              <div className="mt-2 flex gap-2">
                <Button size="sm" onClick={() => responderIntercambio(i, true)} className="gap-1 bg-green-600"><Check className="h-4 w-4" /> Aceptar</Button>
                <Button size="sm" variant="outline" onClick={() => responderIntercambio(i, false)} className="gap-1 border-red-200 text-red-600"><X className="h-4 w-4" /> Rechazar</Button>
              </div>
            </div>
          ))}
        </div>
      )}

      {saldoExtra && (
        <div className="rounded-2xl border border-violet-200 bg-gradient-to-br from-violet-50 to-fuchsia-50/50 p-4">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <p className="text-sm font-semibold text-violet-900">Horas extra autorizadas para compensar</p>
              <p className="font-display text-2xl font-light text-violet-800">
                {fmtHoras(saldoExtra.disponible_segundos || 0)}
              </p>
              <p className="text-xs text-violet-600/80">
                Solo cuenta lo validado por tu encargado. Periodo {fmtFecha(saldoExtra.periodo_desde)} — {fmtFecha(saldoExtra.periodo_hasta)}
              </p>
            </div>
            <Button onClick={() => setCompOpen(true)} className="bg-violet-600 hover:bg-violet-700">
              Solicitar compensación
            </Button>
          </div>
        </div>
      )}

      {compPendConfirmar.length > 0 && (
        <div className="rounded-2xl border-2 border-violet-300 bg-violet-50 p-4 shadow-sm">
          <p className="text-sm font-semibold text-violet-900">Compensaciones propuestas por tu encargado</p>
          {compPendConfirmar.map((c) => (
            <div key={c.id} className="mt-3 rounded-xl border border-violet-200 bg-white p-3">
              <p className="font-medium">
                {c.tipo_compensacion === "economica" ? "Compensación económica" : "Tiempo libre"} · {fmtHoras(c.segundos)}
              </p>
              <p className="text-sm text-zinc-500">
                {fmtFecha(c.fecha_disfrute_inicio)} — {fmtFecha(c.fecha_disfrute_fin)}
              </p>
              {c.motivo && <p className="mt-1 text-xs text-zinc-500">{c.motivo}</p>}
              {c.importe_centimos > 0 && (
                <p className="text-sm font-medium text-emerald-700">{(c.importe_centimos / 100).toFixed(2)} €</p>
              )}
              <div className="mt-2 flex gap-2">
                <Button size="sm" onClick={() => confirmarCompensacion(c, true)} className="gap-1 bg-green-600"><Check className="h-4 w-4" /> Aceptar</Button>
                <Button size="sm" variant="outline" onClick={() => setCompConfirmOpen(c)} className="gap-1 border-red-200 text-red-600"><X className="h-4 w-4" /> Rechazar</Button>
              </div>
            </div>
          ))}
        </div>
      )}

      {pendientesConfirmacion.length > 0 && (
        <div className="rounded-2xl border-2 border-indigo-300 bg-indigo-50 p-4 shadow-sm">
          <p className="text-sm font-semibold text-indigo-900">Peticiones pendientes de tu confirmación</p>
          <div className="mt-3 space-y-3">
            {pendientesConfirmacionAgrupadas.map((entrada) => {
              if (entrada.kind === "group") {
                const { items, tipo_label, tipo, dia } = entrada;
                const diaFmt = dia ? fmtFecha(`${dia}T12:00:00`) : null;
                return (
                  <div key={entrada.key} className="rounded-xl border border-indigo-200 bg-white p-3">
                    <div className="flex flex-wrap items-center gap-2">
                      <p className="font-medium">{tipo_label || TIPO_SOLICITUD[tipo]}</p>
                      {diaFmt && <span className="text-xs text-zinc-500">{diaFmt}</span>}
                      <Badge variant="outline" className="text-[10px]">
                        {items.length} propuesta{items.length > 1 ? "s" : ""}
                      </Badge>
                    </div>
                    <ul className="mt-3 space-y-3">
                      {items.map((s) => (
                        <li key={s.id} className="rounded-lg border border-indigo-100 bg-indigo-50/30 p-3">
                          <p className="text-sm text-zinc-600">
                            {s.tipo === "modificacion_fichaje" && s.timestamp_solicitado && (
                              <>Actual: {fmtFecha(s.timestamp_solicitado)} {fmtHora(s.timestamp_solicitado)}
                              {s.tipo_fichaje ? ` (${s.tipo_fichaje})` : ""} · </>
                            )}
                            Propuesta:{" "}
                            {s.timestamp_aprobado
                              ? `${fmtFecha(s.timestamp_aprobado)} ${fmtHora(s.timestamp_aprobado)}`
                              : "—"}
                            {s.tipo_fichaje && s.tipo === "modificacion_fichaje" ? ` (${s.tipo_fichaje})` : ""}
                            {s.tipo_fichaje && s.tipo !== "modificacion_fichaje" ? ` (${s.tipo_fichaje})` : ""}
                          </p>
                          {s.motivo && <p className="mt-1 text-xs text-zinc-500">Motivo: {s.motivo}</p>}
                          {s.comentario_revision && (
                            <p className="mt-1 text-xs text-indigo-700">{s.comentario_revision}</p>
                          )}
                          <div className="mt-2 flex gap-2">
                            <Button
                              size="sm"
                              onClick={() => {
                                setComentarioConf("");
                                setConfirmOpen(s);
                                confirmar(s, true);
                              }}
                              className="gap-1 bg-green-600"
                            >
                              <Check className="h-4 w-4" /> Confirmar
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => {
                                setComentarioConf("");
                                setConfirmOpen(s);
                              }}
                              className="gap-1 border-red-200 text-red-600"
                            >
                              <X className="h-4 w-4" /> Rechazar
                            </Button>
                          </div>
                        </li>
                      ))}
                    </ul>
                  </div>
                );
              }
              const s = entrada.item;
              return (
                <div key={s.id} className="rounded-xl border border-indigo-200 bg-white p-3">
                  <p className="font-medium">{s.tipo_label || TIPO_SOLICITUD[s.tipo]}</p>
                  {(s.tipo === "vacaciones" || s.tipo === "baja" || s.tipo === "asuntos_propios" || s.tipo === "ausencia_justificada") && (
                    <>
                      {s.cambio_periodo?.fecha_inicio_anterior && s.cambio_periodo?.fecha_fin_anterior ? (
                        <div className="mt-1 space-y-1 text-sm">
                          <p className="text-zinc-500">
                            Periodo anterior:{" "}
                            <span className="line-through">
                              {fmtFecha(s.cambio_periodo.fecha_inicio_anterior)} — {fmtFecha(s.cambio_periodo.fecha_fin_anterior)}
                            </span>
                          </p>
                          <p className="font-medium text-zinc-800">
                            Periodo propuesto: {fmtFecha(s.fecha_inicio)} — {fmtFecha(s.fecha_fin)}
                          </p>
                        </div>
                      ) : (
                        <p className="text-sm text-zinc-600">
                          Periodo: {fmtFecha(s.fecha_inicio)} — {fmtFecha(s.fecha_fin)}
                        </p>
                      )}
                    </>
                  )}
                  {s.tipo === "modificacion_fichaje" && s.timestamp_solicitado && (
                    <p className="text-sm text-zinc-500">
                      Actual: {fmtFecha(s.timestamp_solicitado)} {fmtHora(s.timestamp_solicitado)}
                      {s.tipo_fichaje ? ` (${s.tipo_fichaje})` : ""}
                    </p>
                  )}
                  {s.tipo === "modificacion_fichaje" && (
                    <p className="text-sm text-zinc-600">
                      Propuesta:{" "}
                      {s.timestamp_aprobado
                        ? `${fmtFecha(s.timestamp_aprobado)} ${fmtHora(s.timestamp_aprobado)}`
                        : "—"}
                      {s.tipo_fichaje ? ` (${s.tipo_fichaje})` : ""}
                    </p>
                  )}
                  {s.motivo && <p className="mt-1 text-xs text-zinc-500">Motivo: {s.motivo}</p>}
                  {s.comentario_revision && <p className="mt-1 text-xs text-indigo-700">{s.comentario_revision}</p>}
                  <div className="mt-2 flex gap-2">
                    <Button
                      size="sm"
                      onClick={() => {
                        setComentarioConf("");
                        setConfirmOpen(s);
                        confirmar(s, true);
                      }}
                      className="gap-1 bg-green-600"
                    >
                      <Check className="h-4 w-4" /> Confirmar
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => {
                        setComentarioConf("");
                        setConfirmOpen(s);
                      }}
                      className="gap-1 border-red-200 text-red-600"
                    >
                      <X className="h-4 w-4" /> Rechazar
                    </Button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {vacacionesAsignadas.length > 0 && (
        <div className="rounded-2xl border border-amber-100 bg-amber-50/40 p-4">
          <p className="text-sm font-semibold text-amber-950">Mis vacaciones asignadas</p>
          <p className="mb-3 text-xs text-amber-800/80">
            Puedes solicitar cancelar o modificar días antes de que comience el periodo; tu encargado deberá aprobarlo.
          </p>
          <div className="space-y-2">
            {vacacionesAsignadas.map((v) => (
              <div key={v.solicitud_id} className="flex flex-wrap items-center justify-between gap-2 rounded-xl border border-amber-100 bg-white px-4 py-3 text-sm">
                <div>
                  <p className="font-medium text-violet-900">{fmtFecha(v.fecha_inicio)} — {fmtFecha(v.fecha_fin)}</p>
                  {v.motivo && <p className="text-xs text-zinc-500">{v.motivo}</p>}
                  <Badge variant="outline" className={`mt-1 text-[10px] ${ESTADO_BADGE[v.estado] || ""}`}>
                    {ESTADO_SOLICITUD[v.estado] || v.estado}
                  </Badge>
                  {v.puede_ajustar === false && (
                    <p className="mt-1 text-xs text-zinc-500">El periodo ya ha comenzado; no se pueden solicitar ajustes.</p>
                  )}
                </div>
                {v.puede_ajustar !== false && (
                  <Button size="sm" variant="outline" className="gap-1 border-violet-200 text-violet-700" onClick={() => abrirAjusteVacaciones(v)}>
                    <Pencil className="h-3.5 w-3.5" /> Solicitar ajuste
                  </Button>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="grid gap-3 sm:grid-cols-2">
        {saldo && (
          <div className="rounded-xl border border-violet-100 bg-gradient-to-r from-violet-50 to-indigo-50 p-5 shadow-sm" data-testid="saldo-card">
            <p className="text-[11px] font-semibold uppercase tracking-wide text-violet-700">
              Vacaciones {saldo.anio || new Date().getFullYear()}
            </p>
            <p className="mt-1 text-sm text-zinc-600">
              <span className={`font-display text-2xl font-light ${saldo.restantes < 0 ? "text-rose-700" : "text-zinc-900"}`}>
                {saldo.restantes}
              </span>{" "}
              de {saldo.total} días
              {saldo.modo_label && (
                <span className="ml-1 text-xs text-violet-600">
                  ({subtituloVacaciones({
                    dias_anuales: saldo.dias_anuales,
                    meses_trabajados: saldo.meses_trabajados,
                    proporcional: saldo.proporcional,
                    modo_label: saldo.modo_label,
                  })})
                </span>
              )}
            </p>
            {(saldo.del_anio != null || saldo.arrastre) && (
              <p className="mt-1 text-xs text-violet-700/80">
                {saldo.del_anio ?? "—"} del año
                {Number(saldo.arrastre) !== 0 && (
                  <>
                    {" · "}
                    <span className={saldo.arrastre < 0 ? "text-rose-600" : ""}>
                      {saldo.arrastre > 0 ? "+" : ""}
                      {saldo.arrastre} arrastre
                      {saldo.arrastre < 0 ? " (deuda)" : ""}
                    </span>
                  </>
                )}
                {saldo.pendientes > 0 && <> · {saldo.pendientes} en solicitud</>}
                {saldo.anticipo && <> · anticipo {saldo.anio}</>}
              </p>
            )}
          </div>
        )}
        {saldoAsuntos?.activo && (
          <div className="rounded-xl border border-amber-100 bg-gradient-to-r from-amber-50 to-orange-50 p-5 shadow-sm" data-testid="saldo-asuntos-card">
            <p className="text-[11px] font-semibold uppercase tracking-wide text-amber-700">Asuntos propios {saldoAsuntos.anio}</p>
            <p className="mt-1 text-sm text-zinc-600">
              <span className="font-display text-2xl font-light text-zinc-900">{saldoAsuntos.restantes}</span> de {saldoAsuntos.total} días
            </p>
            <p className="mt-0.5 text-xs text-amber-700/80">
              {saldoAsuntos.usados} usados
              {saldoAsuntos.pendientes > 0 ? ` · ${saldoAsuntos.pendientes} pendientes` : ""}
              {" · días laborables"}
            </p>
          </div>
        )}
      </div>

      <div className="space-y-3" data-testid="solicitudes-lista">
        {intercambiosVisibles.length > 0 && (
          <div className="rounded-xl border border-violet-100 bg-violet-50/30 p-4">
            <p className="mb-3 text-sm font-semibold text-violet-900">
              {gestionarZonas ? "Cambios e intercambios de turno/zona" : "Intercambios de turno"}
            </p>
            <div className="space-y-2">
              {intercambiosVisibles.map((i) => (
                <div key={`${i.ambito}-${i.id}`} className="rounded-xl border border-zinc-200 bg-white p-4 shadow-sm">
                  <div className="flex items-center justify-between gap-2">
                    <Badge variant="outline" className="border-violet-200 bg-violet-50 text-violet-800">
                      {i.ambito === "turno" ? "Intercambio turno" : i.tipo === "intercambio" ? "Intercambio zona" : "Cambio zona"}
                    </Badge>
                    <Badge variant="outline" className={ESTADO_INTERCAMBIO_BADGE[i.estado] || ""}>
                      {ESTADO_INTERCAMBIO[i.estado] || i.estado}
                    </Badge>
                  </div>
                  <p className="mt-2 text-sm text-zinc-600">
                    {fmtFecha(i.fecha_inicio)}
                    {i.fecha_fin !== i.fecha_inicio ? ` — ${fmtFecha(i.fecha_fin)}` : ""}
                    {i.usuario_intercambio_nombre ? ` · con ${i.usuario_intercambio_nombre}` : ""}
                    {i.zona_destino_nombre ? ` → ${i.zona_destino_nombre}` : ""}
                  </p>
                  {i.motivo && <p className="mt-1 text-sm text-zinc-500">{i.motivo}</p>}
                  {i.pareja_comentario && <p className="mt-1 text-xs text-zinc-500">Compañero: {i.pareja_comentario}</p>}
                  {i.comentario_revision && <p className="mt-1 text-xs text-zinc-500">Encargado: {i.comentario_revision}</p>}
                </div>
              ))}
            </div>
          </div>
        )}
        {solicitudesHistorial.length > 0 && (
          <div className="flex flex-wrap items-center gap-2" data-testid="solicitudes-filtros">
            {[
              { id: "todas", label: "Todas" },
              { id: "pendiente", label: "Pendientes" },
              { id: "aprobada", label: "Aprobadas" },
              { id: "rechazada", label: "Rechazadas" },
            ].map((f) => (
              <Button
                key={f.id}
                size="sm"
                variant={filtroEstado === f.id ? "default" : "outline"}
                onClick={() => setFiltroEstado(f.id)}
                data-testid={`solicitudes-filtro-${f.id}`}
                className={`rounded-full ${filtroEstado === f.id ? "bg-indigo-600 hover:bg-indigo-700" : "border-zinc-200 text-zinc-600"}`}
              >
                {f.label}
                <span className={`ml-1.5 rounded-full px-1.5 text-xs ${filtroEstado === f.id ? "bg-white/20" : "bg-zinc-100 text-zinc-500"}`}>
                  {conteosEstado[f.id] ?? 0}
                </span>
              </Button>
            ))}
          </div>
        )}
        {solicitudesHistorial.length > 0 && solicitudesAgrupadas.length === 0 ? (
          <div className="rounded-xl border border-dashed border-zinc-200 bg-white px-5 py-10 text-center text-sm text-zinc-500">
            No hay solicitudes {filtroEstado === "pendiente" ? "pendientes" : filtroEstado === "aprobada" ? "aprobadas" : "rechazadas"}.
          </div>
        ) : (
          solicitudesAgrupadas.map((entrada) => renderSolicitudEntrada(entrada, cargar))
        )}
      </div>

      <Dialog open={!!confirmOpen} onOpenChange={(v) => !v && setConfirmOpen(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {confirmOpen?.tipo === "modificacion_fichaje" ? "Confirmar modificación de fichaje" : "Confirmar propuesta del encargado"}
            </DialogTitle>
          </DialogHeader>
          <Textarea placeholder="Comentario opcional" value={comentarioConf} onChange={(e) => setComentarioConf(e.target.value)} />
          <DialogFooter className="gap-2 sm:gap-0">
            <Button variant="outline" onClick={() => confirmar(confirmOpen, false)} className="border-red-200 text-red-600">Rechazar propuesta</Button>
            <Button onClick={() => confirmar(confirmOpen, true)} className="bg-green-600 hover:bg-green-700">
              {confirmOpen?.tipo === "modificacion_fichaje" ? "Aceptar modificación" : "Aceptar y registrar fichaje"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={horaExtraOpen} onOpenChange={setHoraExtraOpen}>
        <DialogContent className="max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Solicitar autorización de horas extra</DialogTitle>
          </DialogHeader>
          <p className="text-xs text-zinc-500">
            {horaExtraModo === "flexible_periodo" ? (
              <>
                En horario flexible las horas dentro del objetivo semanal/mensual cuentan como normales.
                Solo se listan periodos con exceso sobre ese objetivo mayor de {horaExtraUmbral} minutos.
                Hasta que tu encargado o admin lo apruebe, ese exceso no es compensable.
              </>
            ) : (
              <>
                Solo se listan días con exceso sobre el horario previsto mayor de {horaExtraUmbral} minutos.
                No aparecen días con picado de entrada sin salida. Hasta que tu encargado o admin lo apruebe,
                ese tiempo no cuenta como hora extra compensable.
              </>
            )}
          </p>
          {horaExtraCargando ? (
            <p className="py-6 text-center text-sm text-zinc-500">
              {horaExtraModo === "flexible_periodo" ? "Cargando periodos…" : "Cargando días…"}
            </p>
          ) : horaExtraDias.length === 0 ? (
            <p className="rounded-lg border border-dashed border-zinc-200 px-4 py-8 text-center text-sm text-zinc-500">
              {horaExtraModo === "flexible_periodo"
                ? "No hay periodos con exceso sobre el objetivo pendiente de solicitar."
                : "No hay días con exceso pendiente de solicitar."}
            </p>
          ) : (
            <div className="space-y-2 max-h-56 overflow-y-auto">
              {horaExtraDias.map((d) => {
                const id = d.periodo_inicio || d.fecha;
                const esPeriodo = d.modo === "flexible_periodo" || !!d.periodo_inicio;
                return (
                  <button
                    key={id}
                    type="button"
                    onClick={() => setHoraExtraDia(id)}
                    className={`flex w-full items-center justify-between rounded-lg border px-3 py-2 text-left text-sm transition ${
                      horaExtraDia === id
                        ? "border-orange-300 bg-orange-50 text-orange-950"
                        : "border-zinc-200 bg-white hover:bg-zinc-50"
                    }`}
                  >
                    <span className="font-medium">
                      {esPeriodo
                        ? `${d.etiqueta_periodo === "mes" ? "Mes" : "Semana"} ${fmtFecha(d.periodo_inicio || d.fecha)}${
                            d.periodo_fin ? ` — ${fmtFecha(d.periodo_fin)}` : ""
                          }`
                        : fmtFecha(d.fecha)}
                    </span>
                    <span className="text-xs text-zinc-600">
                      {d.horas_exceso || fmtHoras(d.segundos_exceso)} (objetivo {d.horas_previstas})
                    </span>
                  </button>
                );
              })}
            </div>
          )}
          <div className="space-y-1.5">
            <Label>Motivo de la autorización</Label>
            <Textarea
              placeholder={
                horaExtraModo === "flexible_periodo"
                  ? "Describe el motivo del exceso sobre el objetivo (mín. 3 caracteres)"
                  : "Describe por qué se prolongó la jornada (mín. 3 caracteres)"
              }
              value={horaExtraMotivo}
              onChange={(e) => setHoraExtraMotivo(e.target.value)}
            />
          </div>
          <DialogFooter>
            <Button
              onClick={solicitarHoraExtra}
              disabled={!horaExtraDia || horaExtraCargando}
              className="bg-orange-600 hover:bg-orange-700"
            >
              Enviar solicitud
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={compOpen} onOpenChange={(o) => { setCompOpen(o); if (!o) setCompCalculo(null); }}>
        <DialogContent>
          <DialogHeader><DialogTitle>Solicitar compensación de horas extra</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1.5">
              <Label>Modalidad</Label>
              <Select value={compForm.modalidad} onValueChange={(v) => setCompForm({ ...compForm, modalidad: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="horas">Horas sueltas</SelectItem>
                  <SelectItem value="dia">Día completo</SelectItem>
                  <SelectItem value="periodo">Periodo</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {compForm.modalidad === "horas" && (
              <div className="space-y-1.5">
                <Label>Horas a compensar</Label>
                <Input type="number" min="0.25" step="0.25" value={compForm.horas} onChange={(e) => setCompForm({ ...compForm, horas: e.target.value })} />
              </div>
            )}
            {compForm.modalidad !== "horas" && (
              <p className="text-xs text-violet-700">Las horas se calculan según tu horario en cada día laborable.</p>
            )}
            <div className="grid grid-cols-2 gap-2">
              <div className="space-y-1.5">
                <Label>Disfrute desde</Label>
                <Input type="date" value={compForm.fecha_disfrute_inicio} onChange={(e) => setCompForm({ ...compForm, fecha_disfrute_inicio: e.target.value })} />
              </div>
              <div className="space-y-1.5">
                <Label>Disfrute hasta</Label>
                <Input type="date" value={compForm.fecha_disfrute_fin} onChange={(e) => setCompForm({ ...compForm, fecha_disfrute_fin: e.target.value })} />
              </div>
            </div>
            <CompensacionSaldoPanel
              modalidad={compForm.modalidad}
              horas={compForm.horas}
              fechaDisfruteInicio={compForm.fecha_disfrute_inicio}
              fechaDisfruteFin={compForm.fecha_disfrute_fin}
              periodoDesde={saldoExtra?.periodo_desde}
              periodoHasta={saldoExtra?.periodo_hasta}
              onCalculo={setCompCalculo}
            />
            <div className="space-y-1.5">
              <Label>Motivo</Label>
              <Textarea value={compForm.motivo} onChange={(e) => setCompForm({ ...compForm, motivo: e.target.value })} />
            </div>
          </div>
          <DialogFooter>
            <Button onClick={solicitarCompensacion} disabled={compCalculo?.excede} className="bg-violet-600 hover:bg-violet-700">Enviar a mi encargado</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!vacAjusteOpen} onOpenChange={(o) => !o && setVacAjusteOpen(null)}>
        <DialogContent className="max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Solicitar ajuste de vacaciones</DialogTitle>
          </DialogHeader>
          {vacAjusteOpen && (
            <div className="space-y-3 text-sm">
              <p className="rounded-lg bg-violet-50 px-3 py-2 text-violet-900">
                Periodo actual: {fmtFecha(vacAjusteOpen.fecha_inicio)} — {fmtFecha(vacAjusteOpen.fecha_fin)}
              </p>
              <div className="space-y-1.5">
                <Label>Tipo de ajuste</Label>
                <Select
                  value={vacAjusteForm.accion}
                  onValueChange={(v) => setVacAjusteForm({ ...vacAjusteForm, accion: v })}
                >
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="modificar_periodo">Cambiar periodo completo</SelectItem>
                    <SelectItem value="modificar_dias">Mantener solo algunos días</SelectItem>
                    <SelectItem value="cancelar">Cancelar vacaciones</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {vacAjusteForm.accion === "modificar_periodo" && (
                <div className="grid grid-cols-2 gap-2">
                  <div className="space-y-1.5">
                    <Label>Nuevo desde</Label>
                    <Input type="date" value={vacAjusteForm.fecha_inicio} onChange={(e) => setVacAjusteForm({ ...vacAjusteForm, fecha_inicio: e.target.value })} />
                  </div>
                  <div className="space-y-1.5">
                    <Label>Nuevo hasta</Label>
                    <Input type="date" value={vacAjusteForm.fecha_fin} onChange={(e) => setVacAjusteForm({ ...vacAjusteForm, fecha_fin: e.target.value })} />
                  </div>
                </div>
              )}
              {vacAjusteForm.accion === "modificar_dias" && (
                <div className="space-y-2 rounded-lg border border-violet-100 bg-violet-50/40 p-3">
                  <p className="text-xs font-medium text-violet-900">Marca los días que deseas mantener</p>
                  <div className="grid max-h-40 grid-cols-2 gap-2 overflow-y-auto">
                    {diasEnRangoISO(vacAjusteOpen.fecha_inicio, vacAjusteOpen.fecha_fin).map((dia) => (
                      <label key={dia} className="flex items-center gap-2 text-xs">
                        <Checkbox
                          checked={vacAjusteForm.dias.includes(dia)}
                          onCheckedChange={(v) => {
                            setVacAjusteForm((prev) => ({
                              ...prev,
                              dias: v ? [...prev.dias, dia].sort() : prev.dias.filter((x) => x !== dia),
                            }));
                          }}
                        />
                        {fmtFecha(dia)}
                      </label>
                    ))}
                  </div>
                </div>
              )}
              <div className="space-y-1.5">
                <Label>Motivo del ajuste</Label>
                <Textarea value={vacAjusteForm.motivo} onChange={(e) => setVacAjusteForm({ ...vacAjusteForm, motivo: e.target.value })} />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button onClick={enviarAjusteVacaciones} className="bg-violet-600 hover:bg-violet-700">
              Enviar a mi encargado
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!compConfirmOpen} onOpenChange={(v) => !v && setCompConfirmOpen(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Rechazar compensación propuesta</DialogTitle></DialogHeader>
          <Textarea placeholder="Motivo del rechazo (opcional)" value={comentarioConf} onChange={(e) => setComentarioConf(e.target.value)} />
          <DialogFooter>
            <Button variant="outline" onClick={() => setCompConfirmOpen(null)}>Cancelar</Button>
            <Button onClick={() => confirmarCompensacion(compConfirmOpen, false)} className="border-red-200 bg-red-600 hover:bg-red-700">Confirmar rechazo</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <IntercambioTurnoDialog open={intercambioOpen} onOpenChange={setIntercambioOpen} onSuccess={cargar} />
    </div>
  );
}
